/* eslint-disable no-param-reassign */
import React from 'react';
import { createRoot } from 'react-dom/client';
import AudioPlayer from '../components/atoms/AudioPlayer/AudioPlayer';

/**
 * Scans a container for div.ql-audio nodes and mounts/updates AudioPlayer React roots.
 * Roots are tracked in rootsRef ({ root, container, url }[]) to reuse existing instances.
 * Stale roots (container no longer in the DOM) are scheduled for unmount.
 *
 * Used by InfoPage (dangerouslySetInnerHTML) and RichText (Quill editor, hasModulesCustom).
 */
export function mountAudioPlayers(container, rootsRef) {
  const activeContainers = new Set();

  container.querySelectorAll('div.ql-audio').forEach((node) => {
    const existing = rootsRef.current.find((e) => e.container === node);
    if (existing) {
      activeContainers.add(node);
      existing.root.render(<AudioPlayer url={existing.url} controls />);
      return;
    }
    const audioEl = node.querySelector('audio');
    // The ql-audio node carries the URL, which is what remains once this function has
    // replaced its children with an AudioPlayer root. The other lookups cover content
    // saved before that attribute existed.
    const url = node.getAttribute('url')
      || audioEl?.getAttribute('url')
      || audioEl?.getAttribute('src')
      || node.firstChild?.getAttribute?.('url');
    if (!url) return;
    activeContainers.add(node);
    const root = createRoot(node);
    root.render(<AudioPlayer url={url} controls />);
    rootsRef.current.push({ root, container: node, url });
  });

  rootsRef.current = rootsRef.current.filter(({ root, container }) => {
    if (activeContainers.has(container)) return true;
    setTimeout(() => { if (container.isConnected) root.unmount(); }, 0);
    return false;
  });
}

/**
 * Unmounts all tracked AudioPlayer roots. Call on component unmount.
 */
export function unmountAudioPlayers(rootsRef) {
  rootsRef.current.forEach(({ root, container }) => {
    setTimeout(() => { if (container.isConnected) root.unmount(); }, 0);
  });
  rootsRef.current = [];
}
